import React from "react";
import { App as AntdApp, Button, Card, DatePicker, Form, Input, Modal, Select, Space, Typography } from "antd";
import collect from "collect.js";
import dayjs from "dayjs";

import { loadOrgTree } from "@modules/org/api/orgApi.js";
import api from "@/api/axios.js";

const { Title } = Typography;

export default function EditFlyerModal({ open, onClose, flyer, onUpdated }) {
	const { message } = AntdApp.useApp();
	const [form] = Form.useForm();
	const [consorzi, setConsorzi] = React.useState([]);
	const [canali, setCanali] = React.useState([]);
	const [orgTree, setOrgTree] = React.useState([]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const hasBeenOpenRef = React.useRef(false);

	React.useEffect(() => {
		(async () => {
			const items = await loadOrgTree();
			setOrgTree(items);
			const cons = items.filter(x => x.type === "CONSORZIO").map(x => ({ label: x.name, value: String(x._id) }));
			setConsorzi(cons);
		})();
	}, []);

	const buildCanali = React.useCallback((items, consorzioId) => {
		if (!consorzioId) return [];
		const cooperative = collect(items).filter(item => String(item.parentId) === String(consorzioId));

		return collect(cooperative).map(cooperativa => {
			const options = collect(items)
				.groupBy('parentId')
				.get(cooperativa._id)
				?.map(item => ({ label: item.name, value: String(item._id) }))
				.all();

			if (!options) return null;

			return {
				label: cooperativa.name,
				title: cooperativa.name,
				options: options
			};
		}).filter().all();
	}, []);

	React.useEffect(() => {
		if (!open) return;
		hasBeenOpenRef.current = true;
	}, [open]);

	React.useEffect(() => {
		if (!open || !flyer || !orgTree.length) return;
		const consorzioId = flyer.consorzioId || null;
		const nextCanali = buildCanali(orgTree, consorzioId);
		setCanali(nextCanali);

		const timer = setTimeout(() => {
			form.setFieldsValue({
				name: flyer.name || "",
				consorzioId: consorzioId || null,
				canaleId: flyer.canaleId || null,
				range: [
					flyer.sellOutFrom ? dayjs(flyer.sellOutFrom) : null,
					flyer.sellOutTo ? dayjs(flyer.sellOutTo) : null
				]
			});
		}, 0);

		return () => clearTimeout(timer);
	}, [open, flyer, orgTree, buildCanali, form]);

	React.useEffect(() => {
		if (open) return;
		if (hasBeenOpenRef.current) {
			form.resetFields();
			setCanali([]);
			setIsSubmitting(false);
			hasBeenOpenRef.current = false;
		}
	}, [open, form]);

	const onConsorzioChange = (id) => {
		form.setFieldsValue({ canaleId: null });
		setCanali(buildCanali(orgTree, id));
	};

	const onFilter = (input, option) => {
		return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
	};

	const onUpdateFlyer = async () => {
		if (isSubmitting || !flyer) return;
		setIsSubmitting(true);

		try {
			const v = await form.validateFields();
			const flyerName = String(v.name || "").trim();

			const payload = {
				name: flyerName,
				consorzioId: v.consorzioId || null,
				canaleId: v.canaleId || null,
				sellOutFrom: v.range?.[0] ? v.range[0].format("YYYY-MM-DD") : null,
				sellOutTo: v.range?.[1] ? v.range[1].format("YYYY-MM-DD") : null
			};

			const flyerId = flyer._id || flyer.id;
			const { data } = await api.patch(`/api/flyers/${flyerId}`, payload);
			if (data?.ok) {
				message.success("Volantino aggiornato");
				onUpdated?.(data.flyer || payload);
				onClose?.();
			} else {
				message.error(data?.error || "Aggiornamento non riuscito");
			}
		}
		catch (error) {
			if (!error?.errorFields) {
				message.error(error?.response?.data?.error || "Aggiornamento non riuscito");
			}
		}
		finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={520}
			maskClosable={!isSubmitting}
			closable={!isSubmitting}
			className="sis-modal"
			title="Modifica Volantino"
			destroyOnHidden
		>
			<Card className="card-elev">
				<Title level={5} style={{ marginBottom: 16 }}>Informazioni Volantino</Title>
				<Form form={form} layout="vertical" onFinish={onUpdateFlyer}>
					<Form.Item
						label="Nome Volantino"
						name="name"
						rules={[{ required: true, whitespace: true, message: "Inserisci il nome del volantino" }]}
					>
						<Input autoFocus placeholder="Es. Volantino Primavera 2025" />
					</Form.Item>
					<Form.Item label="Consorzio" name="consorzioId" rules={[{ required: true, message: "Seleziona un consorzio" }]}>
						<Select placeholder="Seleziona consorzio" showSearch options={consorzi} onChange={onConsorzioChange} allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Canale" name="canaleId" rules={[{ required: true, message: "Seleziona un canale" }]}>
						<Select disabled={!canali.length} placeholder="Seleziona canale" options={canali} showSearch allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Sell-Out" name="range" rules={[{ required: true, message: "Seleziona l'intervallo Sell-Out" }]}>
						<DatePicker.RangePicker format="DD/MM/YYYY" placeholder={["Dal", "Al"]} style={{ width: "100%" }} />
					</Form.Item>
					<Space style={{ width: "100%", justifyContent: "flex-end" }}>
						<Button onClick={onClose} disabled={isSubmitting}>Annulla</Button>
						<Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
							Salva modifiche
						</Button>
					</Space>
				</Form>
			</Card>
		</Modal>
	);
}
